import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Flame, Loader2, LogOut, Mail, Users, HandHeart, Lock, Pencil, Check, X, CalendarDays, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/lib/useSEO";

const EASE = [0.16, 1, 0.3, 1];

function formatMoney(cents) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100);
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "America/Chicago" });
}

function formatApiError(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
}

export default function AdminPage() {
  useSEO({ title: "Temple Admin | Neem Karoli Baba Mandir", noindex: true });
  const [auth, setAuth] = useState(null); // null = checking, false = logged out, object = logged in
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("messages");
  const [data, setData] = useState({ messages: [], subscribers: [], donations: [], events: [] });
  const [dataLoading, setDataLoading] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({ title: "", date: "", season: "", text: "" });
  const [savingEvent, setSavingEvent] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setAuth(r.data))
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (!auth) return;
    setDataLoading(true);
    Promise.all([
      api.get("/admin/messages"),
      api.get("/admin/subscribers"),
      api.get("/admin/donations"),
      api.get("/admin/events"),
    ]).then(([m, s, d, ev]) => {
      setData({ messages: m.data.messages, subscribers: s.data.subscribers, donations: d.data.donations, events: ev.data.events });
      setDataLoading(false);
    }).catch(() => setDataLoading(false));
  }, [auth]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/auth/login", { email, password });
      setAuth(res.data.user);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setAuth(false);
  };

  const saveDonorName = async (d) => {
    setSavingName(true);
    try {
      const res = await api.patch(`/admin/donations/${d.session_id}`, { donor_name: editName });
      setData((prev) => ({
        ...prev,
        donations: prev.donations.map((x) =>
          x.session_id === d.session_id ? { ...x, donor_name: res.data.donor_name } : x
        ),
      }));
      toast.success("Donor name updated — the Wall of Grace reflects it now");
      setEditingSession(null);
    } catch {
      toast.error("Could not update the donor name. Please try again.");
    } finally {
      setSavingName(false);
    }
  };

  const saveEvent = async (ev) => {
    setSavingEvent(true);
    try {
      await api.patch(`/admin/events/${ev.slug}`, eventForm);
      setData((prev) => ({
        ...prev,
        events: prev.events.map((x) => (x.slug === ev.slug ? { ...x, ...eventForm } : x)),
      }));
      toast.success("Event updated — the website reflects it now");
      setEditingEvent(null);
    } catch {
      toast.error("Could not update the event. Please try again.");
    } finally {
      setSavingEvent(false);
    }
  };

  const toggleHideEvent = async (ev) => {
    try {
      await api.patch(`/admin/events/${ev.slug}`, { hidden: !ev.hidden });
      setData((prev) => ({
        ...prev,
        events: prev.events.map((x) => (x.slug === ev.slug ? { ...x, hidden: !ev.hidden } : x)),
      }));
      toast.success(ev.hidden ? "Event is visible on the website again" : "Event hidden from the website");
    } catch {
      toast.error("Could not change event visibility. Please try again.");
    }
  };

  const TABS = [
    { id: "messages", label: `Messages (${data.messages.length})`, icon: Mail, testid: "admin-tab-messages" },
    { id: "subscribers", label: `Satsang List (${data.subscribers.length})`, icon: Users, testid: "admin-tab-subscribers" },
    { id: "donations", label: `Donations (${data.donations.length})`, icon: HandHeart, testid: "admin-tab-donations" },
    { id: "events", label: `Events (${data.events.length})`, icon: CalendarDays, testid: "admin-tab-events" },
  ];

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#1C1917] text-stone-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex items-center gap-3 mb-10">
          <span className="relative w-11 h-11 rounded-full overflow-hidden ring-2 ring-amber-500/50 shrink-0">
            <img src="/images/logo-baba.jpg" alt="Neem Karoli Baba" className="w-full h-full object-cover" />
          </span>
          <div>
            <h1 className="font-display text-xl sm:text-2xl text-amber-100" data-testid="admin-heading">Temple Admin</h1>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-500/80">Neem Karoli Baba Mandir · Osho Kaivalya Dham</p>
          </div>
        </div>

        {auth === null && (
          <div className="flex justify-center py-20" data-testid="admin-checking">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          </div>
        )}

        {auth === false && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
            className="max-w-md mx-auto bg-stone-900/70 border border-amber-100/15 rounded-3xl p-8 sm:p-10"
            data-testid="admin-login-card">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="h-5 w-5 text-amber-500" />
              <h2 className="font-display text-xl text-amber-100">Private Area</h2>
            </div>
            <p className="text-sm text-stone-400 mb-6">This page is for the temple caretaker only.</p>
            <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Email</label>
                <Input data-testid="admin-email-input" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)} autoComplete="username"
                  className="mt-1.5 h-11 rounded-xl bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Password</label>
                <Input data-testid="admin-password-input" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                  className="mt-1.5 h-11 rounded-xl bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600" />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
              <Button data-testid="admin-login-button" type="submit" disabled={loading}
                className="w-full h-11 rounded-full bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enter"}
              </Button>
            </form>
            <a href="/" className="block text-center mt-5 text-xs text-stone-500 hover:text-amber-400 transition-colors">
              ← Back to the temple website
            </a>
          </motion.div>
        )}

        {auth && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <p className="text-sm text-stone-400" data-testid="admin-welcome">
                Jai Shri Ram, <span className="text-amber-300 font-semibold">{auth.name}</span>
              </p>
              <button onClick={logout} data-testid="admin-logout-button"
                className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-amber-300 transition-colors">
                <LogOut className="h-4 w-4" /> Log out
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {TABS.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={t.testid}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    tab === t.id
                      ? "bg-gradient-to-r from-amber-600 to-orange-700 text-white shadow-lg"
                      : "bg-stone-900/70 border border-amber-100/15 text-stone-400 hover:text-amber-200"
                  }`}>
                  <t.icon className="h-4 w-4" /> {t.label}
                </button>
              ))}
            </div>

            {dataLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-amber-500" /></div>
            ) : (
              <div className="space-y-4" data-testid="admin-content">
                {tab === "messages" && (
                  data.messages.length === 0
                    ? <EmptyState text="No messages yet — when devotees write through the contact form, they appear here." />
                    : data.messages.map((m, i) => (
                      <div key={i} data-testid={`admin-message-${i}`}
                        className="bg-stone-900/70 border border-amber-100/10 rounded-2xl p-6">
                        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                          <p className="font-semibold text-amber-100">{m.name} <span className="text-stone-500 font-normal">· {m.email}</span></p>
                          <p className="text-xs text-stone-500">{formatDate(m.created_at)}</p>
                        </div>
                        <p className="text-sm text-stone-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                        <a href={`mailto:${m.email}`} className="mt-3 inline-block text-xs font-semibold text-amber-500 hover:text-amber-300 transition-colors">
                          Reply by email →
                        </a>
                      </div>
                    ))
                )}

                {tab === "subscribers" && (
                  data.subscribers.length === 0
                    ? <EmptyState text="No subscribers yet — the satsang list form is in the website footer." />
                    : data.subscribers.map((s, i) => (
                      <div key={i} data-testid={`admin-subscriber-${i}`}
                        className="bg-stone-900/70 border border-amber-100/10 rounded-2xl px-6 py-4 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm"><span className="text-amber-100 font-medium">{s.name || "—"}</span> <span className="text-stone-400">· {s.email}</span></p>
                        <p className="text-xs text-stone-500">{formatDate(s.subscribed_at)}</p>
                      </div>
                    ))
                )}

                {tab === "donations" && (
                  data.donations.length === 0
                    ? <EmptyState text="No donations yet — when a sevak gives through the website, it appears here." />
                    : data.donations.map((d, i) => (
                      <div key={i} data-testid={`admin-donation-${i}`}
                        className="bg-stone-900/70 border border-amber-100/10 rounded-2xl px-6 py-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm flex flex-wrap items-center">
                            <span className="text-amber-100 font-semibold">{formatMoney(d.amount)}</span>
                            {d.recurring && <span className="text-amber-400 text-xs font-semibold"> / month</span>}
                            {editingSession === d.session_id ? (
                              <span className="inline-flex items-center gap-2 ml-2">
                                <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                                  placeholder="Name for the Wall of Grace"
                                  data-testid={`admin-donor-name-input-${i}`}
                                  className="h-8 w-52 rounded-lg bg-stone-950 border-stone-700 text-stone-100 text-xs focus-visible:ring-amber-600" />
                                <button type="button" onClick={() => saveDonorName(d)} disabled={savingName}
                                  data-testid={`admin-donor-name-save-${i}`} aria-label="Save donor name"
                                  className="text-green-400 hover:text-green-300 transition-colors">
                                  {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                </button>
                                <button type="button" onClick={() => setEditingSession(null)}
                                  data-testid={`admin-donor-name-cancel-${i}`} aria-label="Cancel editing"
                                  className="text-stone-500 hover:text-stone-300 transition-colors">
                                  <X className="h-4 w-4" />
                                </button>
                              </span>
                            ) : (
                              <span className="text-stone-400">
                                {" "}· {d.donor_name || "Anonymous Sevak"}
                                <button type="button" onClick={() => { setEditingSession(d.session_id); setEditName(d.donor_name || ""); }}
                                  data-testid={`admin-donor-name-edit-${i}`} aria-label="Edit donor name"
                                  className="ml-2 inline-flex align-middle text-stone-500 hover:text-amber-400 transition-colors">
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </span>
                            )}
                          </p>
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                            d.payment_status === "paid" ? "bg-green-900/50 text-green-300" : "bg-stone-800 text-stone-400"
                          }`} data-testid={`admin-donation-status-${i}`}>
                            {d.payment_status}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2 mt-1.5">
                          <p className="text-xs text-stone-500">
                            {d.lookup_key}{d.seva_label ? ` · ${d.seva_label}` : ""}{d.dedication ? ` · "${d.dedication}"` : ""}
                          </p>
                          <p className="text-xs text-stone-500">{formatDate(d.created_at)}</p>
                        </div>
                      </div>
                    ))
                )}

                {tab === "events" && (
                  data.events.length === 0
                    ? <EmptyState text="No events yet." />
                    : data.events.map((ev, i) => (
                      <div key={ev.slug} data-testid={`admin-event-${i}`}
                        className="bg-stone-900/70 border border-amber-100/10 rounded-2xl p-6">
                        {editingEvent === ev.slug ? (
                          <div className="space-y-3">
                            <div className="grid sm:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Title</label>
                                <Input value={eventForm.title}
                                  onChange={(e) => setEventForm((f) => ({ ...f, title: e.target.value }))}
                                  data-testid={`admin-event-title-input-${i}`}
                                  className="mt-1.5 h-10 rounded-lg bg-stone-950 border-stone-700 text-stone-100 text-sm focus-visible:ring-amber-600" />
                              </div>
                              <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">{ev.kind === "featured" ? "Date" : "Season"}</label>
                                <Input value={ev.kind === "featured" ? eventForm.date : eventForm.season}
                                  onChange={(e) => setEventForm((f) => ev.kind === "featured" ? { ...f, date: e.target.value } : { ...f, season: e.target.value })}
                                  data-testid={`admin-event-date-input-${i}`}
                                  className="mt-1.5 h-10 rounded-lg bg-stone-950 border-stone-700 text-stone-100 text-sm focus-visible:ring-amber-600" />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Description</label>
                              <textarea value={eventForm.text} rows={3}
                                onChange={(e) => setEventForm((f) => ({ ...f, text: e.target.value }))}
                                data-testid={`admin-event-text-input-${i}`}
                                className="mt-1.5 w-full rounded-lg bg-stone-950 border border-stone-700 text-stone-100 text-sm p-3 focus:outline-none focus:ring-2 focus:ring-amber-600" />
                            </div>
                            <div className="flex items-center gap-3">
                              <Button onClick={() => saveEvent(ev)} disabled={savingEvent} data-testid={`admin-event-save-${i}`}
                                className="rounded-full bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white text-sm font-semibold px-5 h-9">
                                {savingEvent ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Save</>}
                              </Button>
                              <button onClick={() => setEditingEvent(null)} data-testid={`admin-event-cancel-${i}`}
                                className="text-sm text-stone-500 hover:text-stone-300 transition-colors">
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="font-semibold text-amber-100">
                                {ev.title} <span className="text-stone-500 font-normal text-sm">· {ev.kind === "featured" ? ev.date : ev.season}</span>
                                {ev.hidden && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold bg-stone-800 text-stone-400 px-2 py-0.5 rounded-full"
                                    data-testid={`admin-event-hidden-badge-${i}`}>Hidden</span>
                                )}
                              </p>
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => { setEditingEvent(ev.slug); setEventForm({ title: ev.title, date: ev.date || "", season: ev.season || "", text: ev.text }); }}
                                  data-testid={`admin-event-edit-${i}`} aria-label={`Edit ${ev.title}`}
                                  className="p-2 rounded-lg text-stone-500 hover:text-amber-400 hover:bg-stone-800 transition-colors">
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button onClick={() => toggleHideEvent(ev)} data-testid={`admin-event-hide-${i}`}
                                  aria-label={ev.hidden ? `Show ${ev.title}` : `Hide ${ev.title}`}
                                  className="p-2 rounded-lg text-stone-500 hover:text-amber-400 hover:bg-stone-800 transition-colors">
                                  {ev.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                              </div>
                            </div>
                            <p className="text-sm text-stone-400 mt-1.5 leading-relaxed">{ev.text}</p>
                          </>
                        )}
                      </div>
                    ))
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="text-center py-16 border border-dashed border-amber-100/15 rounded-2xl" data-testid="admin-empty-state">
      <Flame className="flame mx-auto h-8 w-8 text-amber-600/60 mb-3" />
      <p className="text-sm text-stone-500 max-w-sm mx-auto">{text}</p>
    </div>
  );
}
